import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ToastController } from '@ionic/angular';
import { concat, interval } from 'rxjs';
import { first, filter } from 'rxjs/operators';

/**
 * La aplicación se sirve con un service worker: una pestaña abierta sigue
 * ejecutando la versión que cacheó y no ve un despliegue nuevo hasta que se
 * cierran todas. Eso dejaba a la distribuidora imprimiendo documentos viejos
 * sin enterarse, así que aquí se avisa y se recarga en cuanto hay versión nueva.
 */
@Injectable({ providedIn: 'root' })
export class ActualizacionesService {
  private readonly sw = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly toasts = inject(ToastController);

  private readonly CADA = 30 * 60 * 1000;

  vigilar(): void {
    if (!this.sw.isEnabled) return;

    // Preguntar antes de que la app esté estable compite con el arranque, así
    // que la primera comprobación espera a que lo esté.
    const estable = this.appRef.isStable.pipe(first((estable) => estable));
    concat(estable, interval(this.CADA)).subscribe(() => {
      this.sw.checkForUpdate().catch(() => undefined);
    });

    this.sw.versionUpdates
      .pipe(filter((evento) => evento.type === 'VERSION_READY'))
      .subscribe(() => this.ofrecerRecarga());

    // La caché quedó inservible (ficheros que ya no están en el servidor):
    // sin recargar, la aplicación se queda a medias.
    this.sw.unrecoverable.subscribe(() => location.reload());
  }

  private async ofrecerRecarga(): Promise<void> {
    const toast = await this.toasts.create({
      message: 'Hay una versión nueva de la aplicación.',
      position: 'bottom',
      buttons: [{ text: 'Actualizar', handler: () => void this.recargar() }],
    });
    await toast.present();
  }

  private async recargar(): Promise<void> {
    try {
      await this.sw.activateUpdate();
    } finally {
      location.reload();
    }
  }
}
